<template>
  <div class="relative" @keydown="auClavier">
    <AltInput
      :id="id"
      :model-value="modelValue"
      :state="state"
      :type="type"
      autocomplete="off"
      :placeholder="placeholder"
      role="combobox"
      :aria-expanded="ouvert && afficherListe"
      :aria-controls="`${id}-suggestions`"
      aria-autocomplete="list"
      @update:model-value="saisir"
      @focus="ouvrir"
      @blur="fermerPlusTard"
    />

    <ul
      v-if="ouvert && afficherListe"
      :id="`${id}-suggestions`"
      role="listbox"
      :aria-label="libelleListe"
      class="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg dark:border-alt-dark-border dark:bg-alt-dark-surface"
      :class="{ 'sm:min-w-[24rem]': !compact }"
    >
      <li class="flex items-center gap-1.5 border-b border-slate-100 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-steel-azure dark:border-alt-dark-border dark:text-alt-dark-link">
        <span aria-hidden="true">◆</span> {{ libelleListe }}
      </li>
      <li v-if="recherche" class="px-3 py-2 text-xs text-slate-400">Recherche…</li>
      <li v-else-if="indisponible" class="px-3 py-2 text-xs text-slate-400">{{ libelleIndisponible }}</li>
      <li v-else-if="resultats.length === 0" class="px-3 py-2 text-xs text-slate-400">{{ libelleVide }}</li>
      <li
        v-for="(item, i) in resultats"
        v-else
        :key="cle(item)"
        role="option"
        :aria-selected="i === actif"
        :aria-disabled="estDesactive(item)"
        class="px-3 py-1.5 text-sm"
        :class="[
          estDesactive(item) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          i === actif ? 'bg-slate-100 dark:bg-alt-dark-raised/60' : 'hover:bg-slate-50 dark:hover:bg-alt-dark-raised/40',
        ]"
        @mousedown.prevent="choisir(item)"
        @mouseenter="actif = i"
      >
        <slot name="option" :item="item" :desactive="estDesactive(item)" />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts" generic="T">
import { computed, ref, shallowRef } from 'vue';
import { ApiError } from '@/lib/api';

/**
 * UN CHAMP QUI PROPOSE, AU FIL DE LA FRAPPE, ce que l'annuaire connaît déjà
 * (clients du CRM, sociétés, sites et salariés du groupe). Le champ reste
 * LIBRE : ce qui manque à l'annuaire se saisit à la main.
 *
 * Choisir une proposition émet `choisi` avec l'objet complet : c'est la page
 * qui décide quoi en recopier, et elle n'écrase jamais une saisie sans le dire.
 * Une proposition `desactive` (déjà ouverte dans l'outil) se voit mais ne se
 * choisit pas : on ne crée pas deux fois le même compte.
 */
const props = withDefaults(
  defineProps<{
    modelValue: string;
    chercher: (terme: string) => Promise<T[]>;
    cle: (item: T) => string;
    id?: string;
    state?: 'default' | 'error';
    type?: string;
    placeholder?: string;
    desactive?: (item: T) => boolean;
    min?: number;
    libelleListe?: string;
    libelleVide?: string;
    libelleIndisponible?: string;
    /** Liste à la largeur du champ, sans s'élargir : pour un champ logé dans un menu étroit. */
    compact?: boolean;
  }>(),
  {
    id: () => `sugg-${Math.random().toString(36).slice(2, 8)}`,
    state: 'default',
    type: 'text',
    placeholder: '',
    desactive: undefined,
    min: 2,
    libelleListe: 'Annuaire',
    libelleVide: 'Rien dans l’annuaire : saisissez librement.',
    libelleIndisponible: 'Annuaire indisponible : saisissez librement.',
    compact: false,
  },
);
const emit = defineEmits<{
  'update:modelValue': [value: string];
  choisi: [item: T];
}>();

const resultats = shallowRef<T[]>([]);
const recherche = ref(false);
const indisponible = ref(false);
const ouvert = ref(false);
const actif = ref(-1);
let minuteur: ReturnType<typeof setTimeout> | undefined;
let fermeture: ReturnType<typeof setTimeout> | undefined;
let derniere = 0;

const afficherListe = computed(() => props.modelValue.trim().length >= props.min);
const estDesactive = (item: T): boolean => props.desactive?.(item) ?? false;

function ouvrir(): void {
  // Une fermeture programmée par un blur précédent ne doit pas refermer la liste qu'on rouvre.
  if (fermeture) clearTimeout(fermeture);
  ouvert.value = true;
}

function saisir(v: string): void {
  emit('update:modelValue', v);
  ouvrir();
  actif.value = -1;
  if (minuteur) clearTimeout(minuteur);
  if (v.trim().length < props.min) {
    resultats.value = [];
    return;
  }
  // On n'interroge pas l'annuaire à chaque touche.
  recherche.value = true;
  minuteur = setTimeout(() => void lancer(v.trim()), 250);
}

async function lancer(terme: string): Promise<void> {
  // Une réponse lente ne doit pas écraser celle d'une frappe plus récente.
  const numero = ++derniere;
  try {
    const liste = await props.chercher(terme);
    if (numero !== derniere) return;
    resultats.value = liste;
    indisponible.value = false;
  } catch (e) {
    if (numero !== derniere) return;
    // 503 = `core` non accessible sur cette instance : la saisie reste libre.
    indisponible.value = e instanceof ApiError && e.status === 503;
    resultats.value = [];
  } finally {
    if (numero === derniere) recherche.value = false;
  }
}

function choisir(item: T): void {
  if (estDesactive(item)) return;
  emit('choisi', item);
  ouvert.value = false;
  resultats.value = [];
}

function auClavier(e: KeyboardEvent): void {
  if (!ouvert.value || !resultats.value.length) return;
  const n = resultats.value.length;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    actif.value = (actif.value + 1) % n;
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    actif.value = actif.value <= 0 ? n - 1 : actif.value - 1;
  } else if (e.key === 'Enter' && actif.value >= 0) {
    // Sans ce preventDefault, Entrée soumettrait le formulaire.
    e.preventDefault();
    choisir(resultats.value[actif.value]!);
  } else if (e.key === 'Escape') {
    ouvert.value = false;
  }
}

function fermerPlusTard(): void {
  // Laisse au clic sur une proposition (mousedown) le temps de se déclencher.
  fermeture = setTimeout(() => (ouvert.value = false), 150);
}
</script>
